import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Monitor, Search, ShoppingCart, BarChart3, Users, Globe, Clock, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from '../components/modals/QuoteModal';

const B2BPlatformPage = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const platformFeatures = [
    {
      icon: Search,
      title: 'Advanced Material Search',
      description: 'AI-powered search with filters for specifications, certifications, and availability',
      benefits: ['Real-time inventory', 'Specification matching', 'Price comparison']
    },
    {
      icon: ShoppingCart,
      title: 'Streamlined Ordering',
      description: 'One-click ordering with bulk discounts and automated purchase workflows',
      benefits: ['Bulk pricing tiers', 'Recurring orders', 'Purchase approval flows']
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Comprehensive insights into spending, supplier performance, and market trends',
      benefits: ['Spend analysis', 'Supplier scorecards', 'Market intelligence']
    },
    {
      icon: Users,
      title: 'Supplier Network',
      description: 'Direct connection to 150+ verified European construction material suppliers',
      benefits: ['Verified suppliers', 'Direct communication', 'Performance tracking']
    }
  ];

  const digitalServices = [
    {
      title: 'Digital Catalog Management',
      description: 'Centralized catalog with 10,000+ construction materials, real-time pricing, and availability updates.',
      icon: Monitor
    },
    {
      title: 'Automated Procurement',
      description: 'Smart procurement workflows with approval chains, budget controls, and automated reordering.',
      icon: Globe
    },
    {
      title: 'Real-time Tracking',
      description: 'End-to-end visibility of orders from placement to delivery with live status updates.',
      icon: Clock
    },
    {
      title: 'Secure Transactions',
      description: 'Bank-level security with encrypted payments, digital contracts, and compliance documentation.',
      icon: Shield
    }
  ];

  const integrations = [
    { name: 'SAP', type: 'ERP Integration' },
    { name: 'Microsoft Dynamics', type: 'Business Management' },
    { name: 'Oracle', type: 'Enterprise Software' },
    { name: 'Sage', type: 'Accounting Software' },
    { name: 'QuickBooks', type: 'Financial Management' },
    { name: 'Salesforce', type: 'CRM Integration' }
  ];

  const platformStats = [
    { label: 'Active Users', value: '2,500+' },
    { label: 'Monthly Transactions', value: '€45M+' },
    { label: 'Supplier Network', value: '150+' },
    { label: 'Platform Uptime', value: '99.9%' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:bg-gray-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-purple-100 text-purple-800">B2B Digital Platform</Badge>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Digital Marketplace
                <span className="block font-medium">for Construction Materials</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Revolutionary B2B platform connecting construction companies with verified 
                suppliers. Streamline procurement, reduce costs, and accelerate project timelines.
              </p>
              <div className="flex space-x-4">
                <Button 
                  onClick={() => setIsQuoteModalOpen(true)}
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Monitor size={20} className="mr-2" />
                  Request Demo
                </Button>
                <Button size="lg" variant="outline">
                  View Platform
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070" 
                alt="Digital Platform"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Platform Features
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {platformFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} className="text-purple-600" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900">{feature.title}</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Key Benefits:</p>
                      <ul className="space-y-1">
                        {feature.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2"></div>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Digital Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Digital Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {digitalServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Enterprise Integrations
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {integrations.map((integration, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h4 className="font-medium text-gray-900 mb-2">{integration.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    {integration.type}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Stats */}
      <section className="py-16 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-center mb-12">Platform Performance</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {platformStats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-light mb-2">{stat.value}</div>
                <div className="text-purple-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Transform Your Procurement Process
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join 2,500+ construction professionals using our platform to streamline material procurement.
          </p>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-gray-900 hover:bg-gray-800"
          >
            Start Your Digital Transformation
          </Button>
        </div>
      </section>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </div>
  );
};

export default B2BPlatformPage;